import React, { ChangeEvent, useState } from 'react';
import { Select } from 'antd';
import { t } from '../../../../i18n';
import InputFilterItem from "../../components/InputFilterItem";
import { useTranslation } from 'react-i18next';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface AccountsFilterState {
    name: string;
    phone: string;
    vehiclePlate: string;
    dspId?: string;
    verificationStatus?: number;
    idDocumentNumber?: string;
    isBlacklist?: boolean;
    isEmployed?: boolean;
}

interface AccountFilterProps {
    filters?: AccountsFilterState; // 外部传入的 filters，如果未传入则使用内部状态
    setFilters?: (filters: AccountsFilterState) => void; // 外部传入的 setFilters，如果未传入则使用内部状态
    handleFilter: (filters: AccountsFilterState) => void;
    handleResetFilters?: () => void; // 外部传入的重置函数，如果未传入则使用内部逻辑
    // 新增：DSP 下拉相关
    showDspFilter?: boolean;
    dspOptions?: Array<{ value: string; label: string; dspCode: string }>;
    dspLoading?: boolean;
}

const AccountsFilter: React.FC<AccountFilterProps> = ({
    filters: externalFilters,
    setFilters: externalSetFilters,
    handleFilter,
    handleResetFilters: externalHandleResetFilters,
    showDspFilter = false,
    dspOptions = [],
    dspLoading = false
}) => {

    // 使用统一的样式 Hook
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    const col1Labels = [
        t('delivery.personnel.label.name'),
        t('delivery.driver.verificationStatus'),
    ];
    const col2Labels = [
        t('delivery.personnel.label.phone'),
        t('delivery.personnel.label.isEmployed'),
    ];
    const col3Labels = [
        t('delivery.personnel.label.vehiclePlate'),
        t('delivery.driver.isBlacklist'),
    ];
    const col4Labels = [
        t('delivery.driver.idDocumentNumber'),
        t('delivery.personnel.label.company'),
    ];
    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);
    const col4SpanClassName = getSmartLabelClassName(col4Labels);

    // 使用外部传入的 filters 或内部状态
    const [internalFilters, setInternalFilters] = useState<AccountsFilterState>({
        name: '',
        phone: '',
        vehiclePlate: '',
        dspId: undefined,
        verificationStatus: undefined,
        idDocumentNumber: '',
        isBlacklist: false,
        isEmployed: undefined
    });

    const filters = externalFilters ?? internalFilters;
    const setFilters = externalSetFilters ?? setInternalFilters;

    // 重置筛选条件
    const handleResetFilters = () => {
        const defaultFilters = {
            name: '',
            phone: '',
            vehiclePlate: '',
            dspId: undefined,
            verificationStatus: undefined,
            idDocumentNumber: '',
            isBlacklist: false,
            isEmployed: undefined
        };
        setFilters(defaultFilters);
        if (externalHandleResetFilters) {
            externalHandleResetFilters();
        } else {
            handleFilter(defaultFilters);
        }
    };

    // 获取审核状态选项
    const getVerificationStatusOptions = () => [
        { label: t('delivery.driver.verificationStatusNotReady'), value: 0 },
        { label: t('delivery.driver.verificationStatusPending'), value: 1 },
        { label: t('delivery.driver.verificationStatusApproved'), value: 2 },
        { label: t('delivery.driver.verificationStatusRejected'), value: 3 },
    ];

    // 获取在职状态选项
    const getEmploymentStatusOptions = () => [
        { label: t('delivery.personnel.label.employed'), value: true },
        { label: t('delivery.personnel.label.unemployed'), value: false },
    ];


    // 获取账号状态选项
    const getAccountStatusOptions = () => [
        { label: t('delivery.driver.accountStatusNormal'), value: false },
        { label: t('delivery.driver.accountStatusBanned'), value: true },
    ];


    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 - 四等分布局 */}
            <div className="grid grid-cols-4 gap-x-10 gap-y-4 items-start">
                {/* 第一行：姓名、电话、车牌号码、证件编号 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.name')}
                        value={filters.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, name: e.target.value })}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.phone')}
                        value={filters.phone}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, phone: e.target.value })}
                        spanClassName={col2SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.vehiclePlate')}
                        value={filters.vehiclePlate}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, vehiclePlate: e.target.value })}
                        spanClassName={col3SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.driver.idDocumentNumber')}
                        value={filters.idDocumentNumber || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, idDocumentNumber: e.target.value })}
                        spanClassName={col4SpanClassName}
                    />
                </div>

                {/* 第二行：审核状态、在职状态、账号状态、公司 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={col1SpanClassName}>
                            {t('delivery.driver.verificationStatus')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('delivery.driver.filterByVerificationStatus')}
                                value={filters.verificationStatus}
                                options={getVerificationStatusOptions()}
                                onChange={(value) => setFilters({ ...filters, verificationStatus: value })}
                                allowClear
                                size="middle"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={col2SpanClassName}>
                            {t('delivery.personnel.label.isEmployed')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('components.input.select')}
                                value={filters.isEmployed}
                                options={getEmploymentStatusOptions()}
                                onChange={(value) => setFilters({ ...filters, isEmployed: value })}
                                allowClear
                                size="middle"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={col3SpanClassName}>
                            {t('delivery.driver.isBlacklist')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('delivery.driver.filterByAccountStatus')}
                                value={filters.isBlacklist}
                                options={getAccountStatusOptions()}
                                onChange={(value) => setFilters({ ...filters, isBlacklist: value })}
                                allowClear
                                size="middle"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                {showDspFilter ? (
                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={col4SpanClassName}>
                                {t('delivery.personnel.label.company')}
                            </span>
                            <div className={inputContainerClassName}>
                                <Select
                                    placeholder={t('components.input.select')}
                                    value={filters.dspId ?? undefined}
                                    loading={dspLoading}
                                    options={dspOptions}
                                    onChange={(value) => setFilters({ ...filters, dspId: value })}
                                    allowClear
                                    size="middle"
                                    className="w-full"
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="min-w-0"></div>
                )}
            </div>

        </div>
    );
};

export default AccountsFilter;